import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Backdrop,
  CircularProgress
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { themeBackgrounds } from '../../theme/theme';
import { setIsFirstKeyCreated } from '../../features/auth/authSlice';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';
import OtpInput from '../../components/common/OtpInput';

interface ApiKeyData {
  appId: string;
  secretKey: string;
}

const ApiKeys: React.FC = () => {
  const dispatch = useDispatch();
  const [apiKeys, setApiKeys] = useState<ApiKeyData | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'generate' | 'regenerate'>('generate');
  const [otp, setOtp] = useState('');
  const [isOtpPopupOpen, setIsOtpPopupOpen] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Sending OTP...');
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationId = useSelector((state: any) => state.applicationDetails.app_id);

  useEffect(() => {
    const fetchApiKeys = async () => {
      const payload = {
        request_id: "notegtest1",
        timestamp: "2024-08-02T14:48:00Z",
        action: "get_user_credentials",
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: applicationId || '',
          user_id: userDetails?.user_id || '',
        }, REQ_ENC_KEY)
      };

      type ApiResponse = { status: number;[key: string]: any };
      try {
        const response = await httpService.post<ApiResponse>(`${API_BASE_URL}/user/getUserCredentials`, payload);
        if (response.data.status === 200 && response.data.data) {
          const data = response.data.data;
          const decrypted = await decryptResponse<{
            appId: string;
            secretKey: string;
          }>(data, getSessionKey() as string);
          const { appId, secretKey } = decrypted;
          setApiKeys({
            appId: appId,
            secretKey: secretKey,
          });
        } else {
          setApiKeys(null);
        }
      } catch (error) {
        console.log("Error during API call:", error);
      }
    };

    fetchApiKeys();
  }, [applicationId]);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard!`, { position: 'top-right', autoClose: 1500 });
    } catch (err) {
      toast.error('Failed to copy to clipboard', { position: 'top-right', autoClose: 1500 });
    }
  };

  const handleOtpPopupClose = () => {
    setIsOtpPopupOpen(false);
    setOtp('');
    setOtpError('');
  };

  // Step 1: send an OTP to the logged-in user's email before generating/regenerating keys.
  const sendOtp = async () => {
    const emailId = userDetails?.email_id || '';
    if (!emailId) {
      toast.error('Unable to find your email for verification.', { position: 'top-right', autoClose: 1500 });
      return;
    }
    setOpenDialog(false);
    setIsLoading(true);
    setLoadingMessage('Sending OTP...');
    const payload = {
      request_id: 'api_key_otp',
      action: 'send_otp',
      data: await encryptResponse({
        email_id: emailId,
        purpose: 'mfa'
      }, REQ_ENC_KEY)
    };
    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/sendOTP`, payload);
      if (response.data.status === 200) {
        setOtp('');
        setOtpError('');
        setIsOtpPopupOpen(true);
        toast.success('OTP sent successfully!', { position: 'top-right', autoClose: 1500 });
      } else {
        const errorMessage = response.data?.error?.errorDetails || 'Failed to send OTP. Please try again.';
        toast.error(errorMessage, { position: 'top-right', autoClose: 1500 });
      }
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while sending OTP.', { position: 'top-right', autoClose: 1500 });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: verify the OTP, then run the actual generate/regenerate request.
  const verifyOtp = async () => {
    if (otp.length !== 6) {
      setOtpError('Please enter the 6-digit OTP.');
      return;
    }
    const emailId = userDetails?.email_id || '';
    const payload = {
      request_id: 'api_key_otp',
      action: 'verify_otp',
      data: await encryptResponse({
        email_id: emailId,
        otp: otp
      }, REQ_ENC_KEY)
    };
    setIsLoading(true);
    setLoadingMessage('Verifying OTP...');
    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/verifyOTP`, payload);
      if (response.data.status === 200 && response.data.data) {
        const decryptedData = await decryptResponse(response.data.data, REQ_ENC_KEY);
        const is_email_verified = (decryptedData as any).is_email_verified;
        setOtpError('');
        setIsOtpPopupOpen(false);
        setOtp('');
        if (dialogType === 'generate') {
          await handleGenerateKeys(is_email_verified);
        } else {
          await handleRegenerateKeys(is_email_verified);
        }
      } else {
        setOtpError('Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.log(error);
      setOtpError('An error occurred while verifying OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateKeys = async (is_email_verified: any) => {
    const payload = {
      request_id: "Sampark",
      timestamp: "2024-08-02T14:48:00Z",
      action: "generate_user_credentials",
      data: await encryptResponse({
        organization_id: userDetails?.organization_id || '',
        app_id: applicationId || '',
        user_id: userDetails?.user_id || '',
        is_email_verified: is_email_verified
      }, REQ_ENC_KEY)
    };

    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/generateCredentials`, payload);
      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          app_id: string;
          secret_key: string;
        }>(data, getSessionKey() as string);
        const { app_id, secret_key } = decrypted;
        setApiKeys({
          appId: app_id,
          secretKey: secret_key,
        });
        if (!userDetails?.is_first_key_created) {
          dispatch(setIsFirstKeyCreated(true));
        }
        if (userDetails && !userDetails.is_first_key_created) {
          const payload = {
            data: await encryptResponse({
              organization_id: userDetails?.organization_id || '',
              process_data: {
                is_first_key_created: true
              }
            }, REQ_ENC_KEY)
          };
          const response = await httpService.post<any>(`${API_BASE_URL}/user/updateUserProcess`, payload);
          console.log("Update User Process Response:", response);
          if (response.data?.status === 200) {
            dispatch(setIsFirstKeyCreated(true));
          }
        }
        toast.success(response.data.message, { position: 'top-right', autoClose: 1500 });
      } else {
        toast.error('Failed to generate API keys', { position: 'top-right', autoClose: 1500 });
      }
      setOpenDialog(false);
    } catch (error) {
      toast.error('Error generating API keys', { position: 'top-right', autoClose: 1500 });
    }
  };

  const handleRegenerateKeys = async (is_email_verified: any) => {
    const payload = {
      request_id: "notegtest1",
      timestamp: "2024-08-02T14:48:00Z",
      action: "regenerate_user_credentials",
      data: await encryptResponse({
        organization_id: userDetails?.organization_id || '',
        app_id: apiKeys?.appId || '',
        user_id: userDetails?.user_id || '',
        is_email_verified: is_email_verified
      }, REQ_ENC_KEY)
    };

    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/regenerateCredentials`, payload);
      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          app_id: string;
          secret_key: string;
        }>(data, getSessionKey() as string);
        const { app_id, secret_key } = decrypted;
        setApiKeys({
          appId: app_id,
          secretKey: secret_key,
        });
        toast.success(response.data.message, { position: 'top-right', autoClose: 1500 });
      } else {
        toast.error('Failed to regenerate API keys', { position: 'top-right', autoClose: 1500 });
      }
      setOpenDialog(false);
    } catch (error) {
      toast.error('Error regenerating API keys', { position: 'top-right', autoClose: 1500 });
    }
  };

  const handleOpenDialog = (type: 'generate' | 'regenerate') => {
    setDialogType(type);
    setOpenDialog(true);
  };

  const maskSecretKey = (key: string) => {
    if (key.length <= 8) return key;
    return key.substring(0, 8) + '•'.repeat(key.length - 12) + key.substring(key.length - 4);
  };

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <Box sx={{ mb: 2 }}>
        <PageHeader
          heading="API Keys Management"
          subheading="Manage your API keys for secure access and integration"
          variant="gradient"
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          API Keys Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => handleOpenDialog('regenerate')}
            disabled={!apiKeys?.appId}
            sx={{
              background: themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                background: themeBackgrounds.default,
                color: '#ebe1e1ff',
                opacity: 0.8,
              },
            }}
          >
            Regenerate Keys
          </Button>
        </Box>
      </Box>

      {apiKeys ? (
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Application ID
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    value={apiKeys.appId}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: 'monospace', fontSize: '0.9rem' }
                    }}
                    variant="outlined"
                    size="small"
                  />
                  <Tooltip title="Copy App ID">
                    <IconButton
                      onClick={() => copyToClipboard(apiKeys.appId, 'App ID')}
                      color="primary"
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Secret Key
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    value={showSecret ? apiKeys.secretKey : maskSecretKey(apiKeys.secretKey)}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: 'monospace', fontSize: '0.9rem' }
                    }}
                    variant="outlined"
                    size="small"
                  />
                  <Tooltip title={showSecret ? "Hide Secret Key" : "Show Secret Key"}>
                    <IconButton
                      onClick={() => setShowSecret(!showSecret)}
                      color="primary"
                    >
                      {showSecret ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy Secret Key">
                    <IconButton
                      onClick={() => copyToClipboard(apiKeys.secretKey, 'Secret Key')}
                      color="primary"
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Usage Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" paragraph>
                • Use your App ID to identify your application
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Keep your Secret Key confidential and never share it publicly
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Include the App ID in the UI Kit: <code> {apiKeys.appId}</code>
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Include the Secret Key: <code>{showSecret ? apiKeys.secretKey : '[Your Secret Key]'}</code>
              </Typography>
            </Paper>
          </Grid2>
        </Grid2>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No API Keys Generated
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Generate your first set of API keys to start using our services.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('generate')}
            size="large"
          >
            Generate API Keys
          </Button>
        </Paper>
      )}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'generate' ? 'Generate New API Keys' : 'Regenerate API Keys'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {dialogType === 'generate'
              ? 'This will create a new App ID and Secret Key for your application.'
              : 'This will generate new API keys and invalidate your current ones. Make sure to update your applications with the new keys.'
            }
          </Typography>
          {dialogType === 'regenerate' && (
            <Typography variant="body2" color="warning.main" paragraph>
              ⚠️ Warning: Your existing API keys will be permanently invalidated.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={sendOtp}
            variant="contained"
            color={dialogType === 'regenerate' ? 'warning' : 'primary'}
          >
            {dialogType === 'generate' ? 'Generate' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={isLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {loadingMessage}
          </Typography>
        </Box>
      </Backdrop>

      <Dialog
        open={isOtpPopupOpen}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: '500px',
              maxWidth: '90vw'
            }
          }
        }}
      >
        <DialogTitle>Verify Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Enter the 6-digit OTP sent to <strong>{userDetails?.email_id}</strong> to{' '}
            {dialogType === 'generate' ? 'generate' : 'regenerate'} your API keys.
          </Typography>
          <OtpInput
            id="otp"
            value={otp}
            onChange={(value) => {
              setOtp(value);
              if (otpError) {
                setOtpError('');
              }
            }}
            error={!!otpError}
            helperText={otpError}
            autoFocus={isOtpPopupOpen}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOtpPopupClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={verifyOtp} color="primary" disabled={otp.length !== 6}>
            Verify OTP
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeys;
